import { categoryMatches } from '../../lib/categoryTree.ts';
import type { CategoryTreeNode } from '../../lib/categoryTree.ts';
import { formatBuiltByShort } from '../../lib/format.ts';
import { deckModelId, updatedWindowStart, type DeckModelOption } from './useDeckFilters.ts';
import {
  UNCATEGORISED,
  ME,
  type DeckFilterState,
  type DeckSort,
  type DeckStatusFilter,
  type UpdatedWindow,
} from './deckFilterState.ts';
import type { DeckSummary } from '../../../shared/deck.ts';
import type { CategoryColour } from '../../../shared/api.ts';

/**
 * The filter bar's five dropdowns, as data.
 *
 * Every pill in the row is the same thing — an axis name, the value it is
 * currently set to, and a menu of the values it could be set to — so the shape
 * is written once here and rendered five times. Keeping it out of the component
 * is what lets the awkward half (the counts) be asserted directly: a count that
 * disagrees with the grid it promises is a bug nobody reports, they just stop
 * trusting the numbers.
 *
 * Pure: a function of the decks and the current filter state, no React, no
 * clock of its own beyond the `now` a caller may pin.
 *
 * COUNTS ARE FILTER-AWARE (cross-filtered), and that is a deliberate departure
 * from the counts elsewhere on the page. The rule here is: **an option's count
 * is exactly how many decks the grid will show if you pick it, leaving every
 * other axis as it is.** So the Published count inside a folder is that
 * folder's published decks, not the studio's. The alternative — absolute counts
 * over the whole collection — reads as a promise the grid then breaks ("42
 * published" opening onto three cards), and the number people actually want
 * from a menu is a preview of the click.
 *
 * That does mean these numbers can differ from the sidebar's folder counts and
 * from the title row's "62 decks · 12 published · 50 drafts", which are
 * deliberately absolute (see `deckCountsLine`). They answer a different
 * question: those describe what the studio HOLDS, these predict what a click
 * will DO. The cost is one pass over the decks per axis (five passes, tens of
 * decks) — cheap enough that the accurate answer is also the easy one.
 *
 * The one exception is Trash, whose count is absolute because trash REPLACES
 * the grid rather than narrowing it: no other axis applies to it.
 */

/** One row of a pill's menu. `count` is absent where counting is meaningless
 *  (Sort); `colour` carries a folder's registry tint for its dot; `disabled`
 *  marks a choice that would empty the grid. */
export type FilterOption<T> = {
  value: T;
  label: string;
  count?: number;
  colour?: CategoryColour | null;
  disabled?: boolean;
};

/** One pill: what it is called, what it currently reads, and what it offers. */
export type FilterPillModel<T> = {
  /** Stable id for keys, test ids and the URL axis it writes. */
  axis: 'status' | 'folder' | 'model' | 'owner' | 'updated' | 'sort';
  /** The axis name the pill shows first ("Folder"). */
  label: string;
  /** The current value, for `===` comparison against each option. */
  value: T;
  /** What the pill reads right now ("All", "Clients", "Last 7 days"). */
  valueLabel: string;
  options: FilterOption<T>[];
};

export type FilterBarModel = {
  status: FilterPillModel<DeckStatusFilter>;
  folder: FilterPillModel<string | null>;
  model: FilterPillModel<string | null>;
  owner: FilterPillModel<string | null>;
  updated: FilterPillModel<UpdatedWindow>;
  sort: FilterPillModel<DeckSort>;
};

type CountInput = {
  /** The decks the grid draws from — `visibleDecks` from `useDeckFilters`,
   *  i.e. banks already excluded. Trash is not in here. */
  decks: readonly DeckSummary[];
  filters: DeckFilterState;
  /** Injectable clock, for the updated windows. */
  now?: number;
  /** The signed-in person's id, for the `me` owner sentinel — see
   *  `DeckFilterState.owner`. Null resolves to nothing, which is what makes
   *  "My decks" show an empty grid rather than everybody's while /api/me is
   *  still in flight. */
  meId?: string | null;
};

export type FilterBarInput = CountInput & {
  /** The rail's tree, for the folder menu's rows, their order and their tints.
   *  Counts are recomputed here rather than reused: the tree's are absolute. */
  categoryTree: readonly CategoryTreeNode[];
  /** The model roster from `useDeckFilters` — labels and menu order. */
  models: readonly DeckModelOption[];
  /** How many decks are in the trash. Zero hides the Trash option entirely:
   *  an empty bin is not a place anyone wants to be sent. */
  trashCount: number;
  /** The people registry, for the Owner menu. Archived people are dropped
   *  unless they still own something in view: their decks did not go anywhere,
   *  and a row you cannot reach is a deck you cannot find. Optional: without
   *  it the menu is still Anyone and My decks, which is the pair that matters. */
  people?: readonly { id: string; name: string; archived: boolean }[];
};

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

/**
 * The grid's own filter rule, per axis.
 *
 * This mirrors `useDeckFilters`, which owns the real thing. It is duplicated
 * rather than imported because that lives inside one derivation over all five
 * axes at once and these counts need the axes SEPARATELY — "everything except
 * status" is not a question that function can be asked. `__tests__` pins the
 * two together against a shared fixture, so a change to one that isn't made to
 * the other fails loudly rather than quietly miscounting.
 */
const AXIS_MATCHERS: Record<
  'query' | 'status' | 'category' | 'model' | 'owner' | 'updated',
  (deck: DeckSummary, filters: DeckFilterState, now: number, meId: string | null) => boolean
> = {
  query: (d, f) => {
    const q = f.query.trim().toLowerCase();
    if (!q) return true;
    const model = deckModelId(d);
    return (
      d.title.toLowerCase().includes(q) ||
      d.slug.toLowerCase().includes(q) ||
      (!!d.category && d.category.toLowerCase().includes(q)) ||
      (!!model && model.includes(q)) ||
      (formatBuiltByShort(d.builtBy) || '').toLowerCase().includes(q)
    );
  },
  // Trash is not a narrowing of this list at all, so no deck in it passes.
  status: (d, f) => {
    if (f.status === 'trash') return false;
    if (f.status === 'published') return !!d.publishedUrl;
    if (f.status === 'unpublished') return !d.publishedUrl;
    return true;
  },
  category: (d, f) => {
    if (f.category === null) return true;
    if (f.category === UNCATEGORISED) return !d.category;
    return !!d.category && categoryMatches(d.category, f.category as string);
  },
  model: (d, f) => (f.model ? deckModelId(d) === f.model : true),
  owner: (d, f, _now, meId) => {
    if (!f.owner) return true;
    const wanted = f.owner === ME ? meId : f.owner;
    return !!wanted && d.ownerId === wanted;
  },
  updated: (d, f, now) => {
    const since = updatedWindowStart(f.updated, now);
    return since === null || d.lastEditedAt >= since;
  },
};

type Axis = keyof typeof AXIS_MATCHERS;
const AXES = Object.keys(AXIS_MATCHERS) as Axis[];

/** The decks the grid would show for `filters`. Exported because the counts
 *  below are only trustworthy if the integration can check them against it. */
export function deckMatchesFilters(
  deck: DeckSummary,
  filters: DeckFilterState,
  now = Date.now(),
  meId: string | null = null,
): boolean {
  return AXES.every((axis) => AXIS_MATCHERS[axis](deck, filters, now, meId));
}

/** The decks left once every axis EXCEPT `axis` has been applied — the pool an
 *  axis's own menu counts against, which is what makes a count a preview of
 *  the click rather than a fact about the collection. */
function poolFor({ decks, filters, now, meId = null }: CountInput, axis: Axis): DeckSummary[] {
  const clock = now ?? Date.now();
  const others = AXES.filter((a) => a !== axis);
  return decks.filter((d) => others.every((a) => AXIS_MATCHERS[a](d, filters, clock, meId)));
}

/** A choice leading to an empty grid is offered but not clickable — except the
 *  one already chosen, which must stay selectable so the menu can show it. */
function disabledAt(count: number, selected: boolean): boolean | undefined {
  return count === 0 && !selected ? true : undefined;
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<Exclude<DeckStatusFilter, null>, string> = {
  published: 'Published',
  unpublished: 'Unpublished',
  trash: 'Trash',
};

/** What the Status pill reads. "All" is the unfiltered word, matching the
 *  first row of its own menu rather than inventing a second phrasing. */
export function statusValueLabel(status: DeckStatusFilter): string {
  return status ? STATUS_LABELS[status] : 'All';
}

export function statusOptions(input: CountInput, trashCount: number): FilterOption<DeckStatusFilter>[] {
  const pool = poolFor(input, 'status');
  const published = pool.filter((d) => !!d.publishedUrl).length;
  const { status } = input.filters;
  const options: FilterOption<DeckStatusFilter>[] = [
    { value: null, label: 'All', count: pool.length },
    {
      value: 'published',
      label: STATUS_LABELS.published,
      count: published,
      disabled: disabledAt(published, status === 'published'),
    },
    {
      value: 'unpublished',
      label: STATUS_LABELS.unpublished,
      count: pool.length - published,
      disabled: disabledAt(pool.length - published, status === 'unpublished'),
    },
  ];
  // Absolute, and only when there is something in there: the trash is a
  // separate list that replaces the grid, so no other filter narrows it.
  if (trashCount > 0) options.push({ value: 'trash', label: STATUS_LABELS.trash, count: trashCount });
  return options;
}

// ---------------------------------------------------------------------------
// Folder
// ---------------------------------------------------------------------------

/** A child folder's menu label. The tree gives children their leaf name, which
 *  is ambiguous once the row is out of its indentation ("Acme" under what?), so
 *  the menu spells the path with the separator the cards and the move modals
 *  already use. */
export function folderMenuLabel(path: string): string {
  return path.replace('/', ' › ');
}

/** What the Folder pill reads: the leaf, because the pill is narrow and the
 *  parent is usually obvious from the rail beside it. */
export function folderValueLabel(category: string | null): string {
  if (category === null) return 'All';
  if (category === UNCATEGORISED) return 'Uncategorised';
  const slash = category.indexOf('/');
  return slash === -1 ? category : category.slice(slash + 1);
}

export function folderOptions(
  input: CountInput,
  categoryTree: readonly CategoryTreeNode[],
): FilterOption<string | null>[] {
  const pool = poolFor(input, 'category');
  const active = input.filters.category;
  // A deck has one home, so a parent row counts itself plus every deck filed
  // under one of its children, and never claims more than it opens.
  const countIn = (path: string) => pool.filter((d) => !!d.category && categoryMatches(d.category, path)).length;
  const options: FilterOption<string | null>[] = [{ value: null, label: 'All', count: pool.length }];
  for (const parent of categoryTree) {
    const count = countIn(parent.path);
    options.push({
      value: parent.path,
      label: parent.label,
      count,
      colour: parent.colour ?? null,
      disabled: disabledAt(count, active === parent.path),
    });
    for (const child of parent.children) {
      const childCount = countIn(child.path);
      options.push({
        value: child.path,
        label: folderMenuLabel(child.path),
        count: childCount,
        colour: child.colour ?? null,
        disabled: disabledAt(childCount, active === child.path),
      });
    }
  }
  const uncategorised = pool.filter((d) => !d.category).length;
  options.push({
    value: UNCATEGORISED,
    label: 'Uncategorised',
    count: uncategorised,
    colour: null,
    disabled: disabledAt(uncategorised, active === UNCATEGORISED),
  });
  return options;
}

// ---------------------------------------------------------------------------
// Model
// ---------------------------------------------------------------------------

/** What the Model pill reads. A model can still be filtered by URL after the
 *  last deck built with it was deleted, so the raw id is the fallback — an
 *  unexplained pill is worse than an ugly one. */
export function modelValueLabel(model: string | null, models: readonly DeckModelOption[]): string {
  if (!model) return 'Any';
  return models.find((m) => m.id === model)?.label ?? model;
}

export function modelOptions(input: CountInput, models: readonly DeckModelOption[]): FilterOption<string | null>[] {
  const pool = poolFor(input, 'model');
  const active = input.filters.model;
  const counts = new Map<string, number>();
  for (const d of pool) {
    const id = deckModelId(d);
    if (id) counts.set(id, (counts.get(id) || 0) + 1);
  }
  // "Any" counts the whole pool, including decks with no build stamp, so the
  // model rows deliberately do not add up to it: an imported or hand-made deck
  // is in "Any" and in no model.
  const options: FilterOption<string | null>[] = [{ value: null, label: 'Any', count: pool.length }];
  // Roster order (commonest first) is kept rather than re-sorted by the
  // cross-filtered count: a menu whose rows reorder as you filter is a menu you
  // have to re-read every time you open it.
  for (const m of models) {
    const count = counts.get(m.id) || 0;
    options.push({ value: m.id, label: m.label, count, disabled: disabledAt(count, active === m.id) });
  }
  return options;
}

// ---------------------------------------------------------------------------
// Owner
// ---------------------------------------------------------------------------

/** What the Owner pill reads. "My decks" rather than the reader's own name:
 *  the sentinel means whoever is looking, and their name would suggest the
 *  filter is pinned to that person. */
export function ownerValueLabel(owner: string | null, people: readonly { id: string; name: string }[]): string {
  if (!owner) return 'Anyone';
  if (owner === ME) return 'My decks';
  return people.find((p) => p.id === owner)?.name ?? owner;
}

export function ownerOptions(
  input: CountInput,
  people: readonly { id: string; name: string; archived: boolean }[],
): FilterOption<string | null>[] {
  const pool = poolFor(input, 'owner');
  const active = input.filters.owner;
  const counts = new Map<string, number>();
  for (const d of pool) {
    if (d.ownerId) counts.set(d.ownerId, (counts.get(d.ownerId) || 0) + 1);
  }
  const mine = input.meId ? (counts.get(input.meId) ?? 0) : 0;
  const options: FilterOption<string | null>[] = [
    { value: null, label: 'Anyone', count: pool.length },
    // Always offered, even at zero, and never disabled: "I have no decks yet"
    // is a legitimate thing to look at, and a greyed row reads as broken.
    { value: ME, label: 'My decks', count: mine },
  ];
  for (const p of people) {
    if (p.id === input.meId) continue; // covered by My decks
    const count = counts.get(p.id) ?? 0;
    if (p.archived && count === 0 && active !== p.id) continue;
    options.push({ value: p.id, label: p.name, count, disabled: disabledAt(count, active === p.id) });
  }
  // No "Unowned" row. Every deck made before owner-on-create is one, so it
  // would be the biggest row in the menu and it could not be filtered to
  // anyway: `null` is already Anyone. Reassigning those decks is the bulk
  // owner verb's job, not this menu's.
  return options;
}

// ---------------------------------------------------------------------------
// Updated
// ---------------------------------------------------------------------------

/** Spans, not calendar buckets, apart from Today — `updatedWindowStart` is
 *  rolling, and these words have to describe it honestly. The chip row words
 *  the same windows as "Last 7 days"; the menu names them the way the design
 *  does, as a period rather than a range. */
const UPDATED_LABELS: Record<Exclude<UpdatedWindow, null>, string> = {
  today: 'Today',
  week: 'Past week',
  month: 'Past month',
  quarter: 'Past quarter',
};

export function updatedValueLabel(updated: UpdatedWindow): string {
  return updated ? UPDATED_LABELS[updated] : 'Any time';
}

export function updatedOptions(input: CountInput): FilterOption<UpdatedWindow>[] {
  const pool = poolFor(input, 'updated');
  const clock = input.now ?? Date.now();
  const active = input.filters.updated;
  const options: FilterOption<UpdatedWindow>[] = [{ value: null, label: 'Any time', count: pool.length }];
  for (const window of ['today', 'week', 'month', 'quarter'] as const) {
    const since = updatedWindowStart(window, clock);
    const count = since === null ? pool.length : pool.filter((d) => d.lastEditedAt >= since).length;
    options.push({
      value: window,
      label: UPDATED_LABELS[window],
      count,
      disabled: disabledAt(count, active === window),
    });
  }
  return options;
}

// ---------------------------------------------------------------------------
// Sort
// ---------------------------------------------------------------------------

const SORT_LABELS: Record<DeckSort, string> = {
  edited: 'Last edited',
  opened: 'Last opened',
  name: 'Name',
  slides: 'Slides',
};

export function sortValueLabel(sort: DeckSort): string {
  return SORT_LABELS[sort];
}

/**
 * Sort carries no counts: it reorders the same decks rather than choosing
 * between subsets, so a number beside a row would be the same number four
 * times. Nor is any row disabled — "Last opened" is offered even when nothing
 * has been opened, because it still sorts (never-opened decks fall to the end,
 * see `DECK_SORT_COMPARATORS`) and greying it out would look like a fault.
 */
export function sortOptions(): FilterOption<DeckSort>[] {
  return (['edited', 'opened', 'name', 'slides'] as const).map((value) => ({ value, label: SORT_LABELS[value] }));
}

// ---------------------------------------------------------------------------
// The whole bar
// ---------------------------------------------------------------------------

/**
 * All five pills in one call, so the route wires one function rather than
 * five and cannot accidentally feed two of them different deck lists.
 */
export function filterBarModel(input: FilterBarInput): FilterBarModel {
  const { filters, categoryTree, models, trashCount } = input;
  const counting: CountInput = { decks: input.decks, filters, now: input.now, meId: input.meId };
  return {
    status: {
      axis: 'status',
      label: 'Status',
      value: filters.status,
      valueLabel: statusValueLabel(filters.status),
      options: statusOptions(counting, trashCount),
    },
    folder: {
      axis: 'folder',
      label: 'Folder',
      value: filters.category,
      valueLabel: folderValueLabel(filters.category),
      options: folderOptions(counting, categoryTree),
    },
    model: {
      axis: 'model',
      label: 'Model',
      value: filters.model,
      valueLabel: modelValueLabel(filters.model, models),
      options: modelOptions(counting, models),
    },
    owner: {
      axis: 'owner',
      label: 'Owner',
      value: filters.owner,
      valueLabel: ownerValueLabel(filters.owner, input.people ?? []),
      options: ownerOptions(counting, input.people ?? []),
    },
    updated: {
      axis: 'updated',
      label: 'Updated',
      value: filters.updated,
      valueLabel: updatedValueLabel(filters.updated),
      options: updatedOptions(counting),
    },
    sort: {
      axis: 'sort',
      label: 'Sort',
      value: filters.sort,
      valueLabel: sortValueLabel(filters.sort),
      options: sortOptions(),
    },
  };
}
